import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export type MaterialType = 'pdf' | 'xlsx' | 'pptx' | 'zip' | 'link';

export interface MaterialMetadata {
  id: string;
  name: string;
  type: MaterialType;
  routeTo?: string; // Route to navigate for this material
  generatePDF?: boolean; // Whether to generate PDF from current page
  size?: string;
}

// Material ID mapping
export const materialRoutes: Record<string, MaterialMetadata> = {
  // Training Resources
  'Training Seminar Overview': {
    id: 'training-seminar',
    name: 'Training Seminar Overview',
    type: 'link',
    routeTo: '/admin/marketing-strategy?tab=presentations',
  },
  'Training Syllabus': {
    id: 'training-syllabus',
    name: 'Training Syllabus',
    type: 'pdf',
    routeTo: '/admin/marketing-strategy?tab=training',
    generatePDF: true,
  },
  
  // Sales Collateral
  'EquiForge One-Pager': {
    id: 'one-pager',
    name: 'EquiForge One-Pager',
    type: 'pdf',
    routeTo: '/materials/one-pager',
    generatePDF: true,
  },
  'Product Comparison Sheet': {
    id: 'comparison-sheet',
    name: 'Product Comparison Sheet',
    type: 'pdf',
    routeTo: '/materials/comparison-sheet',
    generatePDF: true,
  },
  'ROI Calculator Template': {
    id: 'roi-calculator',
    name: 'ROI Calculator Template',
    type: 'xlsx',
    routeTo: '/materials/roi-calculator',
    generatePDF: true,
  },
  'Feature Benefits Matrix': {
    id: 'feature-matrix',
    name: 'Feature Benefits Matrix',
    type: 'pdf',
    routeTo: '/materials/feature-benefits',
    generatePDF: true,
  },
  
  // Presentations
  'Demo Script & Walkthrough': {
    id: 'demo-script',
    name: 'Demo Script & Walkthrough',
    type: 'pdf',
    routeTo: '/materials/demo-script',
    generatePDF: true,
  },
  
  // Case Studies
  'Developer Success Story': {
    id: 'case-study-developer',
    name: 'Developer Success Story',
    type: 'pdf',
    routeTo: '/materials/case-studies/developer',
    generatePDF: true,
  },
  'Agent Team Case Study': {
    id: 'case-study-agent',
    name: 'Agent Team Case Study',
    type: 'pdf',
    routeTo: '/materials/case-studies/agent',
    generatePDF: true,
  },
  'Investor Case Study': {
    id: 'case-study-investor',
    name: 'Investor Case Study',
    type: 'pdf',
    routeTo: '/materials/case-studies/investor',
    generatePDF: true,
  },
  
  // Training Resources
  'Quick Start Guide': {
    id: 'quick-start',
    name: 'Quick Start Guide',
    type: 'pdf',
    routeTo: '/materials/quick-start',
    generatePDF: true,
  },
  'Best Practices Handbook': {
    id: 'best-practices',
    name: 'Best Practices Handbook',
    type: 'pdf',
    routeTo: '/materials/best-practices',
    generatePDF: true,
  },
  'Certification Study Guide': {
    id: 'certification-guide',
    name: 'Certification Study Guide',
    type: 'pdf',
    routeTo: '/materials/certification',
    generatePDF: true,
  },
  
  // Presentations
  'Sales Pitch Deck': {
    id: 'sales-pitch',
    name: 'Sales Pitch Deck',
    type: 'pdf',
    routeTo: '/materials/sales-pitch',
    generatePDF: true,
  },

  // Visual Assets
  'Infographics Collection': {
    id: 'infographics',
    name: 'Infographics Collection',
    type: 'zip',
    routeTo: '/materials/infographics',
  },
  'Product Screenshots Pack': {
    id: 'screenshots-pack',
    name: 'Product Screenshots Pack',
    type: 'zip',
    routeTo: '/materials/screenshots',
  },
  'Logo & Brand Assets': {
    id: 'brand-assets',
    name: 'Logo & Brand Assets',
    type: 'zip',
    routeTo: '/materials/brand-assets',
  },

  // Video Content Scripts
  'Platform Overview Video': {
    id: 'video-overview',
    name: 'Platform Overview Video Script',
    type: 'pdf',
    routeTo: '/materials/video-scripts/overview',
    generatePDF: true,
  },
  'Feature Demo Playlist': {
    id: 'video-demos',
    name: 'Feature Demo Scripts',
    type: 'pdf',
    routeTo: '/materials/video-scripts/demos',
    generatePDF: true,
  },
  'Customer Testimonials': {
    id: 'video-testimonials',
    name: 'Testimonials Interview Guide',
    type: 'pdf',
    routeTo: '/materials/video-scripts/testimonials',
    generatePDF: true,
  },
};

/**
 * Generate a PDF from an HTML element
 */
export const generatePDFFromElement = async (
  element: HTMLElement,
  filename: string,
  options: {
    scale?: number;
    orientation?: 'portrait' | 'landscape';
    format?: 'a4' | 'letter';
  } = {}
): Promise<Blob> => {
  const {
    scale = 2,
    orientation = 'portrait',
    format = 'letter',
  } = options;

  // Capture element as canvas
  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  // Calculate dimensions
  const imgWidth = format === 'letter' ? 8.5 : 8.27; // inches
  const imgHeight = format === 'letter' ? 11 : 11.69; // inches
  const pageWidth = orientation === 'portrait' ? imgWidth : imgHeight;
  const pageHeight = orientation === 'portrait' ? imgHeight : imgWidth;

  // Create PDF
  const pdf = new jsPDF({
    orientation,
    unit: 'in',
    format,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdfWidth = pageWidth;
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

  let heightLeft = pdfHeight;
  let position = 0;

  // Add first page
  pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
  heightLeft -= pageHeight;

  // Add additional pages if content is longer than one page
  while (heightLeft > 0) {
    position = heightLeft - pdfHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pageHeight;
  }

  return pdf.output('blob');
};

/**
 * Trigger download of a blob
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Get material metadata by name
 */
export const getMaterialMetadata = (materialName: string): MaterialMetadata | undefined => {
  return materialRoutes[materialName];
};

/**
 * Check if material needs component rendering for PDF generation
 */
export const needsComponentRender = (materialId: string): boolean => {
  const metadata = Object.values(materialRoutes).find(m => m.id === materialId);
  return metadata?.generatePDF === true;
};

/**
 * Get filename for a material
 */
export const getMaterialFilename = (materialId: string, extension: string = 'pdf'): string => {
  const metadata = Object.values(materialRoutes).find(m => m.id === materialId);
  if (!metadata) return `equiforge-material.${extension}`;
  
  const cleanName = metadata.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  const timestamp = new Date().toISOString().split('T')[0];
  return `equiforge-${cleanName}-${timestamp}.${extension}`;
};
